import { GridActionsCellItem, GridColDef, GridRowParams, GridRowsProp } from "@mui/x-data-grid";
import { useState, forwardRef, useEffect } from "react";
import Modal, { CommonModalProps } from "..";
import { Clip } from "../../../Core/Api/Types/clip";
import DataTable, { CellSize } from "../../Table/table";
import TableCellDateTime from "../../Table/TableCellDateTime/table-cell-date-time";
import PublishIcon from "@mui/icons-material/Publish";
import { Button, Chip, CircularProgress, Stack, Typography } from "@mui/material";
import { shareService } from "../../../Core/Api/services";
import { ImportClipsRequest } from "../../../Core/Api/Types/import-clips-request";
import useCustomSnackbar from "../../../Utils/Hooks/useCustomSnackbar";
import TableCellTypography from "../../Table/TableCellTypography/table-cell-typography";
import { useTranslation } from "react-i18next";

export type ModalShareClipElementProps = {
    shareId: number;
    clips: Clip[];
    clipsCallback: (clips: number[], linkedAssetId: number) => void;
};

export type ModalShareClipProps = CommonModalProps<ModalShareClipElementProps>;

const ModalShareClip = forwardRef<any, ModalShareClipProps>((props, ref) => {
    const { t } = useTranslation();
    const { closeModal, clips, shareId, clipsCallback } = props;
    const [selectedClips, setSelectedClips] = useState<Clip[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const customSnackbar = useCustomSnackbar();

    const [rows, setDataGrid] = useState<GridRowsProp>([]);
    type Row = (typeof rows)[number];

    const handleCancelClick = () => {
        closeModal();
    };

    const handleImportClip = (clip: Clip) => {
        closeModal();
        let request = new ImportClipsRequest({
            shareId: shareId,
            clips: [clip],
        });
        shareService.ImportClips(request).then((response) => {
            if (response.success) clipsCallback([clip.id], response.objectId ?? -500);

            customSnackbar(t("alert:success.clipImported"), "success");
        });
    };

    const handleImportClips = () => {
        closeModal();
        let request = new ImportClipsRequest({
            shareId: shareId,
            clips: selectedClips,
        });
        shareService.ImportClips(request).then((response) => {
            if (response.success) clipsCallback(request.clips?.map((x) => x.id) ?? [], response.objectId ?? -500);

            customSnackbar(t("alert:success.clipImported"), "success");
        });
    };

    const handleSelection = (newSelectedRows) => {
        const newState: Clip[] = [];
        newSelectedRows.forEach((id) => {
            let match = clips.find((x) => x.id === id);
            if (match) newState.push(match);
        });

        setSelectedClips(newState);
    };

    const columns: GridColDef[] = [
        {
            field: "userName",
            headerName: t("common:creator"),
            flex: CellSize.medium,
            minWidth: CellSize.medium,
            renderCell: (data: Row) => <TableCellTypography data={data.value} sx={{ p: 0.5 }} />,
        },
        {
            field: "date",
            headerName: t("common:date"),
            flex: CellSize.medium,
            minWidth: CellSize.medium,
            renderCell: (data: Row) => <TableCellDateTime data={data} />,
        },
        {
            field: "comment",
            headerName: t("common:comment"),
            flex: CellSize.xlarge,
            minWidth: CellSize.xlarge,
            renderCell: (data: Row) => <TableCellTypography data={data.value} sx={{ p: 0.5 }} />,
        },
        {
            field: "timecodeIn",
            headerName: t("common:in"),
            flex: CellSize.medium,
            minWidth: CellSize.medium,
        },
        {
            field: "timecodeOut",
            headerName: t("common:out"),
            flex: CellSize.medium,
            minWidth: CellSize.medium,
        },
        {
            field: "importedOnAssetId",
            headerName: t("table:headerName.linkedAsset"),
            flex: CellSize.medium,
            minWidth: CellSize.medium,
            renderCell: (data: Row) => {
                return data.value ? (
                    <Chip label={data.value}></Chip>
                ) : (
                    <Typography color="text.tier" variant="body2">
                        {t("modal:body.noLinkedYet")}
                    </Typography>
                );
            },
        },
        {
            field: "actions",
            headerName: "",
            type: "actions",
            flex: CellSize.actionsDefault,
            minWidth: CellSize.actionsDefault,
            align: "right",
            getActions: (params: GridRowParams) => [
                <GridActionsCellItem
                    icon={<PublishIcon sx={{ width: 20, height: 20 }} />}
                    label={t("common:import")}
                    title={t("common:import")}
                    onClick={() => handleImportClip(params.row)}
                    disabled={params?.row?.importedOnAssetId}
                />,
            ],
        },
    ];

    useEffect(() => {
        setDataGrid(clips);
        setIsLoading(false);
    }, []);

    return (
        <Modal
            title={t("modal:title.shareClips")}
            reference={ref}
            closeModal={handleCancelClick}
            width={1000}
            footer={
                <Stack direction="row" justifyContent="space-between">
                    <Button onClick={handleCancelClick}>{t("common:cancel")}</Button>
                    <Button variant="contained" color="primary" onClick={handleImportClips} disabled={selectedClips.length === 0}>
                        {t("button:importSelected")}
                    </Button>
                </Stack>
            }
        >
            {isLoading && (
                <CircularProgress
                    sx={{
                        position: "fixed",
                        top: "50%",
                        left: "50%",
                    }}
                />
            )}

            {!isLoading && (
                <DataTable
                    noControls
                    onRowSelectionModelChange={handleSelection}
                    rows={rows}
                    columns={columns}
                    getRowHeight={() => "auto"}
                    checkboxSelection
                    disableRowSelectionOnClick
                    pagination={false}
                    isRowSelectable={(params: GridRowParams) => !params?.row?.importedOnAssetId}
                    initialState={{
                        sorting: { sortModel: [{ field: "start", sort: "desc" }] },
                        pinnedColumns: { left: ["__check__"], right: ["actions"] },
                    }}
                />
            )}
        </Modal>
    );
});

export default ModalShareClip;
